import { useState } from 'react';
import { IPopularReport } from 'contexts/reporting/types';
import styled from 'styled-components';
import { screenMd, screenSm } from 'constants/styles/breakpoints';
import { getPopularReportImg } from 'utils/imageHandling';
import Icon from 'components/icons';
import ButtonIcon from 'components/buttonIcon';
import Tooltip from 'components/tooltip';
import Popover from 'components/popover';
import Button from 'components/button';
import ReportDetails from './ReportDetails';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  flex: 1 0 0;
  align-self: stretch;

  border-radius: 15px;
  border: 1px solid #f2f2f7;
  background: #fff;
  position: relative;

  padding: 10px;
  min-width: 275px;

  ${screenSm} {
    padding: 0;
    min-width: unset;
  }

  @media (max-width: 991px) {
    min-width: 260px;
    flex: 0 0 auto;
  }
`;

const Schedule = styled.div`
  border-radius: 7px;
  display: flex;
  padding: 5px 10px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  background: rgba(181, 255, 233, 0.5);
  color: ${({ theme }) => theme.default.colors.performance.positiveDark};

  font-size: 12px;
  font-weight: 700;
  line-height: 14px;

  ${screenSm} {
    padding: 8px 15px;
    position: absolute;
    top: 0;
    right: 0;
    border-radius: 0 15px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-items: center;
  height: 100%;

  ${screenSm} {
    width: 100%;
    flex-direction: row;
    padding: 0 15px;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  flex: 1 0 0;
  gap: 5px;
  transition: margin-top 0.2s ease;
  ${Card}:hover & {
    margin-top: 6.5px;
  }
  ${screenSm} {
    margin-top: 35px;
    justify-content: center;
  }
`;

const Title = styled.span`
  overflow: hidden;
  color: var(--black-dark, #1a2535);
  font-feature-settings: 'liga' off, 'clig' off;
  text-overflow: ellipsis;

  font-size: 14px;
  font-weight: 700;
  line-height: 20px;

  ${screenSm} {
    font-size: 16px;
    font-weight: 900;
    line-height: 23px;
  }
`;

const Description = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-feature-settings: 'liga' off, 'clig' off;

  font-size: 12px;
  font-weight: 400;
  line-height: 14px;

  ${screenSm} {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
`;

const Link = styled.a<{ visible?: boolean }>`
  &.desktop {
    display: none !important;
    ${screenSm} {
      display: inherit !important;
    }
  }
  color: var(--lilac-dark, #7965c6);
  font-feature-settings: 'liga' off, 'clig' off;

  font-size: 14px;
  font-weight: 700;
  line-height: 20px;

  /* Hidden by default; shown when Card is hovered */
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  pointer-events: ${(props) => (props.visible ? 'auto' : 'none')};

  /* Hide default underline */
  text-decoration: none;

  transform: translateX(0);
  transition: opacity 0.2s ease-in-out, visibility 0s linear 0.2s, transform 0.2s ease;

  ${Card}:hover & {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    transition: opacity 0.2s ease-in-out, visibility 0s, transform 0.2s ease;
  }
  :hover {
    transform: translateX(5px);
    color: var(--lilac-dark, #7965c6);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  justify-content: space-between;
  span {
    cursor: pointer;
  }
  &.desktop {
    display: none !important;
    ${screenSm} {
      display: flex !important;
    }
  }
  &.mobile {
    ${screenSm} {
      display: none !important;
    }
  }

  ${screenSm} {
    gap: 10px;
  }
`;

const ActionButton = styled(Button)`
  width: 100%;
  ${screenSm} {
    display: none !important;
  }
`;

const Img = styled.img.attrs({
  loading: 'lazy',
  decoding: 'async',
  fetchpriority: 'low',
  width: 53,
  height: 53,
})`
  display: none;
  ${screenMd} {
    display: block;
    flex-shrink: 0;
    aspect-ratio: 1/1;
  }
`;

const ReportCard = ({
  report,
  onGenerateReport,
  onScheduleReport,
  onClick,
}: {
  report: IPopularReport;
  onGenerateReport: (reportId: string, reportType: string) => void;
  onScheduleReport: (report: IPopularReport) => void;
  onClick: (report: IPopularReport) => void;
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  return (
    <Card onClick={() => onClick(report)}>
      {(report?.schedules?.length ?? 0) > 0 && (
        <Schedule>
          <Icon icon="CalendarTransparent" color="#009d6f" size="small" />
          {report.schedules.map((p) => p.frequency).join(', ')}
        </Schedule>
      )}
      <Content>
        <Img src={getPopularReportImg(report.type)} alt="Popular Report" />
        <Details>
          <Title>{report.name}</Title>
          <Description>{report.description}</Description>
          <Popover
            content={
              <ReportDetails
                reportName={report.name}
                reportType={report.type}
                items={report.checkboxes}
              />
            }
            trigger="click"
            placement="right"
            onVisibleChange={(open) => {
              setPopoverOpen(open);
            }}
          >
            <Link className="desktop" visible={popoverOpen} href="#">
              See what’s included
            </Link>
          </Popover>
        </Details>
        <Actions className="desktop">
          <Tooltip title={'Schedule report'}>
            <ButtonIcon
              $terciary
              size="small"
              icon="CalendarTransparent"
              onClick={() => onScheduleReport(report)}
              style={{ boxShadow: 'none' }}
            />
          </Tooltip>
          <Tooltip title={'Generate report'}>
            <ButtonIcon
              $terciary
              size="small"
              icon="PlayTransparent"
              onClick={() => onGenerateReport(report.id, report.type)}
              style={{ boxShadow: 'none' }}
            />
          </Tooltip>
        </Actions>
        <Actions className="mobile">
          <ActionButton onClick={() => onGenerateReport(report.id, report.type)}>
            Generate
          </ActionButton>
          <ActionButton onClick={() => onScheduleReport(report)}>Schedule</ActionButton>
        </Actions>
      </Content>
    </Card>
  );
};

export default ReportCard;
